import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import "./OklchColorPicker.css";
import { hexToOklch, oklchToHex } from "../util/color.ts";

/* In-page OKLCH color picker - three native range sliders (lightness, chroma,
 * hue) plus a hex field, rendered inline instead of the OS-native color dialog.
 * Slider tracks are painted with CSS `oklch()` gradients so they preview the
 * exact ramp; the committed value still round-trips through color.ts so it is
 * always a gamut-fit sRGB hex. Hue is shown in degrees; color.ts works in
 * radians. */

const RAD = Math.PI / 180;
const MAX_CHROMA = 0.4;

type Lch = { l: number; c: number; h: number }; // h in degrees [0, 360)

function fromHex(hex: string): Lch {
  const o = hexToOklch(hex) ?? { L: 0, C: 0, H: 0 };
  let h = (o.H / RAD) % 360;
  if (h < 0) h += 360;
  return { l: o.L, c: o.C, h };
}

function toHex({ l, c, h }: Lch): string {
  return oklchToHex({ L: l, C: c, H: h * RAD });
}

function hueStops(l: number, c: number): string {
  const chroma = Math.max(c, 0.12); // keep the strip colorful even for greys
  return [0, 60, 120, 180, 240, 300, 360].map((d) => `oklch(${l} ${chroma} ${d})`).join(", ");
}

export default function OklchColorPicker({
  value,
  onChange,
  onClose,
  style,
}: {
  value: string;
  onChange: (hex: string) => void;
  onClose: () => void;
  style?: CSSProperties;
}) {
  const [lch, setLch] = useState<Lch>(() => fromHex(value));
  const [hexText, setHexText] = useState(value);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click or Escape.
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  function update(next: Lch) {
    setLch(next);
    const hex = toHex(next);
    setHexText(hex);
    onChange(hex);
  }

  function commitHex(text: string) {
    setHexText(text);
    if (hexToOklch(text)) {
      const next = fromHex(text);
      setLch(next);
      onChange(toHex(next));
    }
  }

  const hex = toHex(lch);
  const { l, c, h } = lch;
  const lTrack = `linear-gradient(to right, oklch(0 ${c} ${h}), oklch(0.5 ${c} ${h}), oklch(1 ${c} ${h}))`;
  const cTrack = `linear-gradient(to right, oklch(${l} 0 ${h}), oklch(${l} ${MAX_CHROMA} ${h}))`;
  const hTrack = `linear-gradient(to right, ${hueStops(l, c)})`;

  return (
    <div ref={ref} className="bg-body border rounded-3 shadow-lg p-2" style={style}>
      <div
        className="rounded-2 border mb-2"
        style={{ height: 28, background: hex }}
        title={hex}
      ></div>

      <Slider
        label="L"
        min={0}
        max={1}
        step={0.001}
        value={l}
        track={lTrack}
        display={l.toFixed(3)}
      >
        {(v) => update({ ...lch, l: v })}
      </Slider>
      <Slider
        label="C"
        min={0}
        max={MAX_CHROMA}
        step={0.001}
        value={c}
        track={cTrack}
        display={c.toFixed(3)}
      >
        {(v) => update({ ...lch, c: v })}
      </Slider>
      <Slider
        label="H"
        min={0}
        max={360}
        step={0.5}
        value={h}
        track={hTrack}
        display={`${Math.round(h)}°`}
      >
        {(v) => update({ ...lch, h: v })}
      </Slider>

      <div className="d-flex align-items-center gap-2 mt-2">
        <input
          type="text"
          className="form-control form-control-sm font-monospace"
          value={hexText}
          spellCheck={false}
          onChange={(e) => commitHex(e.target.value)}
          style={{ fontSize: "0.75rem" }}
        />
        <input
          type="color"
          value={hex}
          onChange={(e) => commitHex(e.target.value)}
          title="System color picker"
          style={{
            width: 28,
            height: 28,
            padding: 0,
            border: "1px solid var(--bs-border-color)",
            borderRadius: 4,
            background: "none",
            cursor: "pointer",
            flexShrink: 0,
          }}
        />
      </div>
    </div>
  );
}

function Slider({
  label,
  min,
  max,
  step,
  value,
  track,
  display,
  children,
}: {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  track: string;
  display: string;
  children: (value: number) => void;
}) {
  return (
    <div className="d-flex align-items-center gap-2 mb-2">
      <span className="small text-muted" style={{ width: 12 }}>
        {label}
      </span>
      <div className="oklch-slider flex-grow-1" style={{ background: track }}>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => children(Number(e.target.value))}
        />
      </div>
      <span className="small text-muted font-monospace text-end" style={{ width: 38 }}>
        {display}
      </span>
    </div>
  );
}
